export const LOAN_ENTRY_CATEGORY = Object.freeze({
    RENTAL: '賃貸',
    ROOM_INITIAL_COST: '部屋初期費用',
    UTILITIES: '電気・ガス費',
    LIVING_EXPENSE: '生活費',
    BICYCLE: '自転車',
    OTHER: '他',
});

export const LOAN_ENTRY_CATEGORIES = Object.freeze([
    LOAN_ENTRY_CATEGORY.RENTAL,
    LOAN_ENTRY_CATEGORY.ROOM_INITIAL_COST,
    LOAN_ENTRY_CATEGORY.UTILITIES,
    LOAN_ENTRY_CATEGORY.LIVING_EXPENSE,
    LOAN_ENTRY_CATEGORY.BICYCLE,
    LOAN_ENTRY_CATEGORY.OTHER,
]);

export const DEFAULT_ADVANCE_LOAN_ENTRY_CATEGORIES = Object.freeze([
    LOAN_ENTRY_CATEGORY.ROOM_INITIAL_COST,
    LOAN_ENTRY_CATEGORY.UTILITIES,
    LOAN_ENTRY_CATEGORY.LIVING_EXPENSE,
    LOAN_ENTRY_CATEGORY.BICYCLE,
    LOAN_ENTRY_CATEGORY.OTHER,
]);

export const RENTAL_CATEGORY = LOAN_ENTRY_CATEGORY.RENTAL;
export const DEFAULT_LOAN_ENTRY_CATEGORY = LOAN_ENTRY_CATEGORY.OTHER;
export const ADVANCE_REPAYMENT_CATEGORY = '立替金';
export const LOAN_SUBJECT_TYPE = Object.freeze({
    INDIVIDUAL: '個人',
    CORPORATE: '法人',
});
export const LOAN_SUBJECT_TYPES = Object.freeze([
    LOAN_SUBJECT_TYPE.INDIVIDUAL,
    LOAN_SUBJECT_TYPE.CORPORATE,
]);
export const DEFAULT_LOAN_SUBJECT_TYPE = LOAN_SUBJECT_TYPE.INDIVIDUAL;

export const REPAYMENT_CATEGORY = Object.freeze({
    RENTAL: RENTAL_CATEGORY,
    ADVANCE: ADVANCE_REPAYMENT_CATEGORY,
});

export const normalizeLoanEntryCategory = (value) => {
    const category = String(value ?? '').trim();
    return category || DEFAULT_LOAN_ENTRY_CATEGORY;
};

export const normalizeLoanSubjectType = (value) => {
    const subjectType = String(value ?? '').trim();
    return LOAN_SUBJECT_TYPES.includes(subjectType)
        ? subjectType
        : DEFAULT_LOAN_SUBJECT_TYPE;
};

export const isDeletedLoanEntry = (entry) => Boolean(entry?.isDeleted ?? entry?.IsDeleted ?? false);

export const getLoanEntrySubjectType = (entry) => (
    normalizeLoanSubjectType(entry?.subjectType ?? entry?.SubjectType)
);

export const getActiveLoanEntries = (loan) => (
    (loan?.loanEntries || loan?.LoanEntries || []).filter(entry => !isDeletedLoanEntry(entry))
);

export const isRentalLoanEntry = (entryOrCategory) => {
    const category = typeof entryOrCategory === 'string'
        ? entryOrCategory
        : entryOrCategory?.category ?? entryOrCategory?.Category;

    return normalizeLoanEntryCategory(category) === RENTAL_CATEGORY;
};

export const getLoanRepayments = (loan) => loan?.repayments || loan?.Repayments || [];

export const getRepaymentSubjectType = (repayment) => (
    normalizeLoanSubjectType(repayment?.subjectType ?? repayment?.SubjectType)
);

export const isRentalRepayment = (repayment) => (
    (repayment?.category ?? repayment?.Category) === REPAYMENT_CATEGORY.RENTAL
);

export const isAdvanceRepayment = (repayment) => !isRentalRepayment(repayment);

export const getLoanFinancialSummary = (loan) => {
    const entries = getActiveLoanEntries(loan);
    const repayments = getLoanRepayments(loan);
    const createEmptyBucket = () => ({
        rentalBorrowed: 0,
        advanceBorrowed: 0,
        rentalRepaid: 0,
        advanceRepaid: 0,
    });
    const finalizeBucket = (bucket) => {
        const rentalRemaining = Math.max(0, bucket.rentalBorrowed - bucket.rentalRepaid);
        const advanceRemaining = Math.max(0, bucket.advanceBorrowed - bucket.advanceRepaid);

        return {
            rentalBorrowed: bucket.rentalBorrowed,
            advanceBorrowed: bucket.advanceBorrowed,
            totalBorrowed: bucket.rentalBorrowed + bucket.advanceBorrowed,
            rentalRepaid: bucket.rentalRepaid,
            advanceRepaid: bucket.advanceRepaid,
            totalRepaid: bucket.rentalRepaid + bucket.advanceRepaid,
            rentalRemaining,
            advanceRemaining,
            totalRemaining: rentalRemaining + advanceRemaining,
        };
    };
    const subjectBuckets = {
        individual: createEmptyBucket(),
        corporate: createEmptyBucket(),
    };
    const getSubjectKey = (subjectType) => (
        normalizeLoanSubjectType(subjectType) === LOAN_SUBJECT_TYPE.CORPORATE
            ? 'corporate'
            : 'individual'
    );

    entries.forEach((entry) => {
        const subjectKey = getSubjectKey(getLoanEntrySubjectType(entry));
        const amount = Number(entry.amount ?? entry.Amount ?? 0);
        if (isRentalLoanEntry(entry)) {
            subjectBuckets[subjectKey].rentalBorrowed += amount;
            return;
        }
        subjectBuckets[subjectKey].advanceBorrowed += amount;
    });

    repayments.forEach((repayment) => {
        const subjectKey = getSubjectKey(getRepaymentSubjectType(repayment));
        const amount = Number(repayment.amount ?? repayment.Amount ?? 0);
        if (isRentalRepayment(repayment)) {
            subjectBuckets[subjectKey].rentalRepaid += amount;
            return;
        }
        subjectBuckets[subjectKey].advanceRepaid += amount;
    });

    const subjectTotals = {
        individual: finalizeBucket(subjectBuckets.individual),
        corporate: finalizeBucket(subjectBuckets.corporate),
    };
    const totalBucket = finalizeBucket({
        rentalBorrowed: subjectTotals.individual.rentalBorrowed + subjectTotals.corporate.rentalBorrowed,
        advanceBorrowed: subjectTotals.individual.advanceBorrowed + subjectTotals.corporate.advanceBorrowed,
        rentalRepaid: subjectTotals.individual.rentalRepaid + subjectTotals.corporate.rentalRepaid,
        advanceRepaid: subjectTotals.individual.advanceRepaid + subjectTotals.corporate.advanceRepaid,
    });
    subjectTotals.total = totalBucket;

    return {
        ...totalBucket,
        subjectTotals,
    };
};
